(() => {
  if (window.CLPremiumGallery) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const normal = value => String(value || '').normalize('NFKC').replace(/&/g, ' and ').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  const id = value => String(value ?? '');

  function imageLabel(image) {
    const match = String(image.alt || '').match(/^(.*) in (.+),\s*image\s+\d+\b/i);
    return match ? { product: normal(match[1]), colour: normal(match[2]) } : null;
  }

  function resolve(data, variantId, colour) {
    const images = data.images || [];
    const variants = data.variantImages || [];
    const variant = variants.find(v => id(v.id) === id(variantId));
    const anchor = images.find(image => id(image.id) === id(variant?.imageId));
    const wanted = normal(colour);
    const safe = image => {
      const label = imageLabel(image);
      if (wanted && label && label.colour !== wanted) return false;
      const owners = (image.variantIds || []).map(value => variants.find(v => id(v.id) === id(value))).filter(Boolean);
      return owners.every(owner => !wanted || !owner.colour || normal(owner.colour) === wanted);
    };
    const associated = images.filter(image => safe(image) && (image === anchor || (image.variantIds || []).some(value => id(value) === id(variantId))));
    const productNames = new Set([data.title, ...(data.titleAliases || [])].map(normal));
    const verified = images.filter(image => safe(image) && (variant?.verifiedMediaIds || []).some(value => id(value) === id(image.id)));
    if (verified.length) return { images: verified, reason: 'visually_verified_product_form', source: 'verified_display_mapping' };
    const labelled = wanted && data.title ? images.filter(image => {
      const label = imageLabel(image);
      return productNames.has(label?.product) && label.colour === wanted && safe(image);
    }) : [];
    if (associated.length) {
      const peers = variants.filter(v => normal(v.colour) === wanted);
      const sharedAnchor = peers.length > 0 && peers.every(v => v.imageId && id(v.imageId) === id(variant?.imageId));
      if (data.hasMultipleFormats && !sharedAnchor) return { images: associated, reason: 'format_requires_explicit_association', source: 'shopify_association' };
      const selected = new Set([...associated, ...labelled]);
      return { images: images.filter(image => selected.has(image)), reason: null, source: 'shopify_association' };
    }
    if (data.hasMultipleFormats) return { images: [], reason: 'format_requires_explicit_association', source: 'placeholder' };
    if (labelled.length) return { images: labelled, reason: 'verified_colour_fallback', source: 'exact_product_colour_label' };
    return { images: [], reason: anchor && !safe(anchor) ? 'contradictory_variant_image' : 'missing_variant_image', source: 'placeholder' };
  }

  function sourceSet(image) {
    if (image.srcset) return image.srcset;
    const maximum = Math.min(2000, image.width || 1600);
    return [...new Set([360, 540, 720, 1000, 1400, 1600].filter(w => w < maximum).concat(maximum))].map(width => {
      const url = new URL(image.url, location.origin);
      url.searchParams.set('width', width);
      return `${url.href} ${width}w`;
    }).join(', ');
  }

  class Gallery extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.loads = new Map();
      this.animations = [];
      this.token = 0;
      this.index = 0;
      this.images = [];
      this.dataset.swipe = this.config.swipe === false ? 'off' : 'on';
      this.tabIndex = this.config.swipe === false ? -1 : 0;
      this.setAttribute('role', 'group');
      this.setAttribute('aria-label', this.config.label);
      this.dataset.clPremium = '';
      this.viewport = document.createElement('span');
      this.viewport.className = 'clpg-viewport';
      this.progress = document.createElement('span');
      this.progress.className = 'clpg-progress';
      this.progress.setAttribute('aria-hidden', 'true');
      this.fill = document.createElement('span');
      this.progress.append(this.fill);
      this.status = document.createElement('span');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('aria-live', 'polite');
      this.empty = document.createElement('span');
      this.empty.className = 'clpg-empty';
      this.empty.setAttribute('role', 'img');
      this.empty.setAttribute('aria-label', 'Image unavailable');
      this.empty.textContent = 'Image unavailable';
      this.append(this.viewport);
      if (this.config.swipe !== false) this.append(this.progress, this.status);
      const options = { signal: this.events.signal };
      this.addEventListener('pointerdown', e => this.down(e), options);
      this.addEventListener('pointermove', e => this.move(e), options);
      this.addEventListener('pointerup', e => this.up(e), options);
      this.addEventListener('pointercancel', () => this.cancelDrag(), options);
      this.addEventListener('lostpointercapture', e => {
        if (e.target === this && this.drag?.pointer === e.pointerId) this.cancelDrag();
      }, options);
      this.addEventListener('dragstart', e => e.preventDefault(), options);
      this.addEventListener('click', e => {
        if (performance.now() < (this.suppressUntil || 0)) {
          e.preventDefault(); e.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener('keydown', e => {
        if (this.config.swipe === false) return;
        const next = e.key === 'ArrowRight' ? this.index + 1 : e.key === 'ArrowLeft' ? this.index - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? this.images.length - 1 : null;
        if (next === null || this.images.length < 2) return;
        e.preventDefault(); e.stopPropagation(); this.show(next);
      }, options);
      this.addEventListener('pointerenter', e => {
        if (this.config.card && fine.matches && e.pointerType === 'mouse') this.hover(true);
      }, options);
      this.addEventListener('pointerleave', () => this.hover(false), options);
      reduced.addEventListener('change', () => this.finishAnimations(), options);
      this.resize = new ResizeObserver(() => { if (this.drag) this.cancelDrag(); });
      this.resize.observe(this);
    }

    disconnectedCallback() {
      this.token++;
      this.events?.abort(); this.events = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.finishAnimations();
      this.loads?.clear();
      this.replaceChildren();
      this.config.onDisconnect?.();
    }

    finishAnimations() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations = [];
      this.outgoing?.remove(); this.outgoing = null;
      this.viewport?.querySelectorAll('.clpg-slide').forEach(slide => { slide.style.transform = ''; });
      this.removeAttribute('data-clpg-hover');
    }

    async image(info) {
      const key = id(info.id);
      if (this.loads.has(key)) return this.loads.get(key);
      const promise = (async () => {
        const image = new Image();
        image.alt = info.alt || this.config.label;
        image.width = info.width || 1600; image.height = info.height || 1600;
        image.decoding = 'async'; image.draggable = false;
        image.dataset.clGalleryImage = ''; image.dataset.clFrontSelected = 'true';
        image.sizes = this.config.sizes;
        const loaded = new Promise((accept, reject) => {
          const signal = this.events.signal;
          const cleanup = () => { clearTimeout(timeout); image.onload = null; image.onerror = null; signal.removeEventListener('abort', abort); };
          const abort = () => { cleanup(); image.removeAttribute('srcset'); image.removeAttribute('src'); reject(new Error('Gallery disconnected')); };
          const timeout = setTimeout(() => { cleanup(); reject(new Error('Image timeout')); }, 15000);
          image.onload = () => { cleanup(); accept(); };
          image.onerror = () => { cleanup(); reject(new Error('Image unavailable')); };
          signal.addEventListener('abort', abort, { once: true });
        });
        image.srcset = window.CLStudio?.srcset(sourceSet(info)) || sourceSet(info);
        image.src = window.CLStudio?.url(info.url) || info.url;
        await loaded;
        await image.decode().catch(() => {});
        if (this.config.card && this.isConnected && window.CLCardPresentation) await window.CLCardPresentation.prepare(image, this.config.frame, this.config.card, info.alt);
        return image;
      })();
      this.loads.set(key, promise);
      promise.catch(() => this.loads.delete(key));
      return promise;
    }

    makeSlide(image, position) {
      const slide = document.createElement('span');
      slide.className = 'clpg-slide';
      slide.style.setProperty('--clpg-position', position);
      slide.append(image.cloneNode(true));
      return slide;
    }

    async select(images, fade = true) {
      const token = ++this.token;
      this.cancelDrag(); this.finishAnimations();
      this.pending = true;
      this.setAttribute('aria-busy', 'true');
      this.progress.hidden = true;
      if (!images.length) {
        this.images = []; this.index = 0; this.viewport.replaceChildren(this.empty);
        this.pending = false; this.removeAttribute('aria-busy'); this.dataset.count = '0';
        this.config.frame?.classList.add('clpg-ready');
        this.mark(); return;
      }
      try {
        const image = await this.image(images[0]);
        if (token !== this.token || !this.isConnected) return;
        const old = this.viewport.querySelector('.clpg-slide[data-current]');
        if (old && fade && !reduced.matches) {
          this.outgoing = old; this.append(old); old.classList.add('clpg-outgoing');
        }
        this.images = images; this.index = 0;
        const slide = this.makeSlide(image, 0); slide.dataset.current = '';
        this.viewport.replaceChildren(slide);
        this.pending = false; this.removeAttribute('aria-busy');
        this.dataset.count = images.length;
        this.config.frame?.classList.add('clpg-ready');
        if (this.outgoing) {
          const old = this.outgoing;
          const a = old.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 400, easing: 'ease-in-out' });
          const b = slide.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 400, easing: 'ease-in-out' });
          this.animations = [a, b];
          a.finished.then(() => { old.remove(); if (this.outgoing === old) this.outgoing = null; }).catch(() => {});
        }
        this.mark(); this.adjacent();
      } catch {
        if (token !== this.token) return;
        this.pending = false; this.removeAttribute('aria-busy');
        this.images = []; this.index = 0; this.viewport.replaceChildren(this.empty); this.dataset.count = '0';
        this.config.frame?.classList.add('clpg-ready');
        this.mark();
        this.config.onError?.();
      }
    }

    mark() {
      this.dataset.index = this.index;
      const interactive = this.config.swipe !== false;
      this.progress.hidden = !interactive || this.images.length < 2;
      this.fill.style.transform = `scaleX(${this.images.length ? (this.index + 1) / this.images.length : 0})`;
      const text = this.images.length ? `${this.index + 1} / ${this.images.length}` : 'Image unavailable';
      this.status.textContent = interactive ? text : '';
      this.setAttribute('aria-label', interactive ? `${this.config.label} · ${text}` : this.config.label);
      this.config.onChange?.(this.index, this.images);
    }

    adjacent() {
      const token = this.token;
      const index = this.index;
      const wanted = (this.config.swipe === false ? fine.matches ? [1] : [] : [index - 1, index + 1]).filter(i => i >= 0 && i < this.images.length);
      this.viewport.querySelectorAll('[data-adjacent]').forEach(slide => slide.remove());
      for (const i of wanted) this.image(this.images[i]).then(image => {
        if (this.token !== token || this.index !== index || !this.isConnected) return;
        const slide = this.makeSlide(image, i - index);
        slide.dataset.adjacent = i;
        this.viewport.append(slide);
      }).catch(() => {});
      const keep = new Set([index, ...wanted].map(i => id(this.images[i]?.id)));
      for (const key of this.loads.keys()) if (!keep.has(key)) this.loads.delete(key);
    }

    async show(value, animate = true) {
      if (this.config.swipe === false) return;
      if (this.pending || !this.images.length) return;
      const next = Math.max(0, Math.min(this.images.length - 1, value));
      const token = this.token;
      const from = this.index;
      const dragOffset = this.dragOffset || 0;
      this.finishAnimations(); this.hover(false);
      if (next === from) { this.dragOffset = 0; this.settle(dragOffset, animate); return; }
      try {
        const image = await this.image(this.images[next]);
        if (token !== this.token || this.index !== from || !this.isConnected) return;
        const old = this.viewport.querySelector('[data-current]');
        const direction = Math.sign(next - from);
        const incoming = this.makeSlide(image, 0); incoming.dataset.current = '';
        this.viewport.replaceChildren(incoming);
        this.index = next; this.dragOffset = 0;
        if (animate && !reduced.matches && old) {
          this.viewport.append(old); this.outgoing = old; old.removeAttribute('data-current'); old.style.setProperty('--clpg-position', -direction);
          const width = this.clientWidth;
          const a = old.animate([{ transform: `translateX(${dragOffset}px)` }, { transform: `translateX(${-direction * width}px)` }], { duration: 360, easing: 'cubic-bezier(.22,.61,.36,1)' });
          const b = incoming.animate([{ transform: `translateX(${direction * width + dragOffset}px)` }, { transform: 'translateX(0)' }], { duration: 360, easing: 'cubic-bezier(.22,.61,.36,1)' });
          this.animations = [a, b];
          a.finished.then(() => old.remove()).catch(() => {});
        }
        this.mark(); this.adjacent();
      } catch { this.settle(dragOffset, animate); }
    }

    settle(offset, animate = true) {
      this.dragOffset = 0;
      const slide = this.viewport.querySelector('[data-current]');
      if (slide && animate && !reduced.matches && offset) this.animations = [slide.animate([{ transform: `translateX(${offset}px)` }, { transform: 'translateX(0)' }], { duration: 340, easing: 'ease-in-out' })];
      this.viewport.style.transform = '';
    }

    down(e) {
      if (this.config.swipe === false) return;
      if (this.pending || this.images.length < 2 || !e.isPrimary || e.button !== 0) return;
      this.finishAnimations(); this.hover(false);
      this.drag = { pointer: e.pointerId, x: e.clientX, y: e.clientY, lastX: e.clientX, time: e.timeStamp, velocity: 0, width: this.clientWidth, locked: false };
    }

    move(e) {
      const drag = this.drag;
      if (!drag || drag.pointer !== e.pointerId) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.locked) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) { this.drag = null; return; }
        drag.locked = true; this.setPointerCapture(e.pointerId);
      }
      e.preventDefault(); e.stopPropagation();
      const elapsed = Math.max(1, e.timeStamp - drag.time);
      drag.velocity = (e.clientX - drag.lastX) / elapsed;
      drag.lastX = e.clientX; drag.time = e.timeStamp;
      this.dragOffset = (this.index === 0 && dx > 0) || (this.index === this.images.length - 1 && dx < 0) ? dx * .22 : dx;
      if (!this.frame) this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.drag?.locked) this.viewport.style.transform = `translate3d(${this.dragOffset}px,0,0)`;
      });
    }

    up(e) {
      const drag = this.drag;
      if (!drag || e.pointerId !== drag.pointer) return;
      this.drag = null;
      if (!drag.locked) return;
      e.preventDefault(); e.stopPropagation();
      cancelAnimationFrame(this.frame); this.frame = 0;
      this.suppressUntil = performance.now() + 450;
      const dx = e.clientX - drag.x;
      const velocity = e.timeStamp - drag.time < 100 ? drag.velocity : 0;
      const projected = dx + velocity * 100;
      const step = Math.abs(projected) > Math.min(80, drag.width * .2) ? -Math.sign(projected) : 0;
      this.viewport.style.transform = '';
      this.show(this.index + step);
      if (this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
    }

    cancelDrag() {
      if (!this.drag) return;
      const drag = this.drag; this.drag = null;
      if (drag.locked) this.suppressUntil = performance.now() + 450;
      cancelAnimationFrame(this.frame); this.frame = 0;
      if (this.hasPointerCapture(drag.pointer)) this.releasePointerCapture(drag.pointer);
      const offset = this.dragOffset || 0;
      this.viewport.style.transform = ''; this.settle(offset);
    }

    async hover(active) {
      this.hovered = active;
      if (!active) { this.removeAttribute('data-clpg-hover'); return; }
      if (this.pending || this.drag || this.images.length < 2 || !fine.matches) return;
      const token = this.token;
      try {
        const image = await this.image(this.images[1]);
        if (token !== this.token || !this.hovered || !this.isConnected || this.drag) return;
        this.hoverLayer?.remove();
        this.hoverLayer = this.makeSlide(image, 0); this.hoverLayer.classList.add('clpg-hover');
        this.append(this.hoverLayer); this.setAttribute('data-clpg-hover', '');
      } catch {}
    }
  }

  customElements.define('cl-premium-gallery', Gallery);

  function create(mount, config) {
    const element = document.createElement('cl-premium-gallery');
    element.config = config; mount.append(element); return element;
  }

  const cards = new WeakMap();
  function initCard(card) {
    if (cards.has(card)) return cards.get(card);
    card.querySelector('cl-premium-gallery')?.remove();
    const dataElement = card.querySelector('[data-cl-hover-data]');
    const layer = dataElement?.closest('[data-cl-hover]');
    const frame = card.querySelector('.clo-pc__ph, .card-gallery, .image-block, .clo-pair__media');
    let primary = frame?.querySelector('[data-cl-hover-primary], img:not([data-cl-hover-secondary])');
    if (!dataElement || !frame) return null;
    let data;
    try { data = JSON.parse(dataElement.textContent); } catch { return null; }
    const mount = frame.querySelector('.clo-pc__image-link') || frame;
    if (mount instanceof HTMLAnchorElement) mount.draggable = false;
    if (!primary) { primary = new Image(); primary.dataset.clHoverPrimary = ''; mount.prepend(primary); }
    frame.dataset.clpgFrame = '';
    primary.dataset.clPremiumBase = '';
    const state = { data, gallery: null, variant: null };
    cards.set(card, state);
    const gallery = create(mount, { card, frame, swipe: document.body.dataset.clPageType !== 'index', label: card.dataset.productTitle || data.title, sizes: primary.sizes || layer.dataset.sizes,
      onDisconnect: () => cards.delete(card),
      onError: () => card.setAttribute('data-cl-image-missing', '') });
    state.gallery = gallery;
    selectCard(card, null, false);
    return state;
  }

  function selectCard(card, details, fade = true) {
    const state = cards.get(card) || initCard(card);
    if (!state) return false;
    const swatch = card.querySelector('[data-cl-card-swatch][aria-pressed="true"]');
    const variant = details?.variantId || card.dataset.selectedVariant || swatch?.dataset.variantId || state.data.initialVariant;
    const colour = details?.colour || swatch?.dataset.colour || state.data.initialColour;
    const result = resolve(state.data, variant, colour);
    card.dataset.clGalleryIssue = result.reason || '';
    card.toggleAttribute('data-cl-image-missing', !result.images.length);
    if (state.variant === id(variant) && !details) return true;
    state.variant = id(variant);
    state.gallery.select(result.images, fade);
    return true;
  }

  const visible = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    visible.unobserve(entry.target); initCard(entry.target);
  }), { rootMargin: '120px' });
  const observed = new WeakSet();
  function scan(root) {
    const candidates = [...root.querySelectorAll('[data-cl-card]')];
    if (root.matches?.('[data-cl-card]')) candidates.push(root);
    for (const card of candidates) if (!observed.has(card)) { observed.add(card); visible.observe(card); }
  }
  function start() {
    scan(document);
    new MutationObserver(entries => {
      for (const entry of entries) {
        for (const node of entry.removedNodes) if (node instanceof Element) {
          if (node.matches('[data-cl-card]')) { visible.unobserve(node); observed.delete(node); }
          node.querySelectorAll('[data-cl-card]').forEach(card => { visible.unobserve(card); observed.delete(card); });
        }
        for (const node of entry.addedNodes) if (node instanceof Element && !node.closest('cl-premium-gallery')) scan(node);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
  window.CLPremiumGallery = { resolve, create, selectCard, initCard };
  document.addEventListener('cl:card-colour', event => {
    const card = event.target instanceof Element && event.target.closest('[data-cl-card]');
    if (card) selectCard(card, null);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
